import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useParams, useSearchParams } from 'react-router-dom';
import { CircleAlert, Plus, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useMe } from '@/hooks/use-me';
import { useObra } from '@/hooks/use-obra';
import { useObraRdts } from '@/hooks/use-obra-rdts';
import { useRdt } from '@/hooks/use-rdt';
import { useArchivarRdt, useBorrarRdt, useCrearRdt } from '@/hooks/use-rdt-mutations';
import { ApiError } from '@/lib/query-client';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Alert, AlertAction, AlertDescription, AlertTitle } from '@/components/reui/alert';
import { HeaderObra } from '@/components/rdt/header-obra';
import { RdtListColumn } from '@/components/rdt/rdt-list-card';
import { Ficha } from '@/components/rdt/ficha';
import { EdicionRdt } from '@/components/rdt/edicion-rdt';
import { Actividad } from '@/components/rdt/actividad';
import { ConfirmDialog, type ConfirmDialogState } from '@/components/rdt/confirm-dialog';
import { Button } from '@/components/ui/button';
import { actividadItems, esPendiente, normalizarNro, ordenarRdts } from '@/components/rdt/rdt-domain';
import type { TipoPaso } from '@/components/rdt/rdt-domain';
import type { ObraDetalle as ObraDetalleTipo, TipoRdt } from '@/types/api';

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message || fallback;
  if (err instanceof Error) return err.message;
  return fallback;
}

type EditMode = null | 'edit' | 'new';
type PasoAbierto = { tipo: Exclude<TipoPaso, 'comentario'>; hitoKey?: string } | null;

// CTA post-salto/calculado (decisión b de Juan 2026-08-28 — SUPERSEDE la
// ventana "Saltó"/"Calculado" EXACTOS de la resolución #11 del panel
// adversarial 2026-07-11): esa ventana era demasiado angosta — se cerraba
// apenas la rede avanzaba UN hito más. Caso real que la mató: obra 1480, la
// #30 saltó y Juan le cargó entrega+aprobación en la misma sentada (quedó en
// 'Aprobado') → CTA invisible, con la obra SIN ninguna Provisoria en ciclo:
// justo el hueco de radar que el CTA existe para cubrir.
//
// Regla nueva: el CTA se muestra mientras la rede seleccionada esté en
// CUALQUIER estado post-salto (Provisoria) / post-cálculo (Definitiva) SIN
// llegar a Facturado, no esté archivada, y no quede otra rede viva del mismo
// tipo en ciclo. Se arma con los campos YA derivados por el backend
// (backend/src/domain/rdt-estado.ts, RdtEstado — espejados 1:1 en
// RdtEstadoDerivado de types/api.ts) en vez de enumerar nombres de hito:
//   - `salto` (Provisoria): no-null desde que el chequeo salta y SIGUE
//     no-null en todos los hitos posteriores — derivarEstado() devuelve el
//     mismo `salto` tanto en la rama "sin hitos" como en la rama "con hitos
//     completados" (el salto "quedó atrás" pero el dato no se pierde). Post-
//     salto = `salto !== null`, sin importar cuántos hitos avanzó después.
//   - `estado !== 'A calcular'` (Definitiva): 'A calcular' es el ÚNICO
//     estado pre-cálculo posible (derivarEstado(), rama Definitiva: sin
//     hitos y sin montos informados) — cualquier otro valor (Calculado o el
//     nombre de un hito) ya es post-cálculo. No hay un booleano dedicado
//     como `salto` para este caso, así que se compara contra el único string
//     "de antes" en vez de listar los "de después".
//   - `finalizada` (ambos tipos): true solo con Facturado completo — apaga
//     el CTA (una obra con todo facturado NO lo muestra; ese radar es del
//     Avivador futuro, no ensuciar las obras terminadas).
interface CtaSiguienteInfo {
  label: string;
  tipo: TipoRdt;
}

interface RdtParaCta {
  id: number;
  tipo: TipoRdt;
  estado: string;
  archivada: boolean;
  finalizada: boolean;
  salto: { periodo: string } | null;
}

function ctaSiguiente(actual: RdtParaCta, todas: RdtParaCta[]): CtaSiguienteInfo | null {
  if (actual.archivada || actual.finalizada) return null;

  if (actual.tipo === 'Provisoria') {
    if (!actual.salto) return null; // todavía no saltó (A chequear / Sin salto)
    // 'A chequear' / 'Sin salto': ESTADO_A_CHEQUEAR / ESTADO_SIN_SALTO de
    // backend/src/domain/rdt-estado.ts — los únicos 2 estados de una
    // Provisoria que TODAVÍA no saltó (de OTRA rede `r`, no de `actual`,
    // que ya se filtró arriba), por eso se enumeran a mano acá.
    const sigueEnCiclo = todas.some(
      r => r.id !== actual.id && r.tipo === 'Provisoria' && !r.archivada && (r.estado === 'A chequear' || r.estado === 'Sin salto'),
    );
    if (!sigueEnCiclo) return { label: 'Crear la Rede siguiente', tipo: 'Provisoria' };
  }

  if (actual.tipo === 'Definitiva') {
    if (actual.estado === 'A calcular') return null; // todavía no calculó
    const sigueEnCiclo = todas.some(r => r.id !== actual.id && r.tipo === 'Definitiva' && !r.archivada && r.estado === 'A calcular');
    if (!sigueEnCiclo) return { label: 'Crear la Definitiva siguiente', tipo: 'Definitiva' };
  }

  return null;
}

// Alta ultra-mínima del CTA: precarga tipo + responsable (de la RDT que
// saltó/calculó) y pide SOLO el número — un paso menos que "+ Nueva".
function CtaProximaRdt({
  obra,
  cta,
  responsableId,
  onCreada,
}: {
  obra: ObraDetalleTipo;
  cta: CtaSiguienteInfo;
  responsableId: number | null;
  onCreada: (id: number) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const [nro, setNro] = useState('');
  const crearRdt = useCrearRdt();

  function handleCrear(): void {
    const nroTrim = nro.trim();
    if (!nroTrim) return;
    crearRdt.mutate(
      { cc: obra.cc, nro: nroTrim, tipo: cta.tipo, responsableId },
      {
        onSuccess: data => {
          // Decisión de Juan 2026-09-14 (ver rdt-domain.ts normalizarNro):
          // mismo criterio que edicion-rdt.tsx — mostrar el nro guardado
          // (ya normalizado), no lo tipeado.
          toast.success(`Rede #${data.nro ?? normalizarNro(nroTrim)} creada`);
          setAbierto(false);
          setNro('');
          onCreada(data.id);
        },
        onError: err => toast.error(errorMessage(err, 'No se pudo crear la redeterminación')),
      },
    );
  }

  return (
    <div className="mb-3.5 flex flex-wrap items-center gap-2.5 rounded-[11px] border border-dashed border-accent-strong/45 bg-accent-strong/6 px-3.5 py-2.5">
      <Sparkles className="h-4 w-4 flex-none text-accent-strong" aria-hidden="true" />
      <p className="m-0 flex-1 text-[12.5px] text-foreground">
        {cta.tipo === 'Provisoria' ? 'Esta Rede ya saltó' : 'Este monto ya está calculado'} y no queda otra {cta.tipo} en ciclo en esta obra.
      </p>
      {abierto ? (
        <div className="flex items-center gap-1.5">
          <Input
            autoFocus
            value={nro}
            onChange={e => setNro(e.target.value)}
            placeholder="Nº de la Rede"
            aria-label="Número de la nueva redeterminación"
            className="h-8 w-[140px] text-xs"
            onKeyDown={e => {
              if (e.key === 'Enter') handleCrear();
            }}
          />
          <Button type="button" size="sm" onClick={handleCrear} disabled={crearRdt.isPending || !nro.trim()}>
            {crearRdt.isPending ? 'Creando…' : 'Crear'}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setAbierto(false)}>
            Cancelar
          </Button>
        </div>
      ) : (
        <Button type="button" size="sm" onClick={() => setAbierto(true)} className="gap-1.5">
          <Plus className="h-3.5 w-3.5" />
          {cta.label}
        </Button>
      )}
    </div>
  );
}

function DetalleSkeleton() {
  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden" aria-hidden="true">
      <span role="status" className="sr-only">
        Cargando obra…
      </span>
      <div className="flex-none border-b border-border-subtle px-[26px] pb-[15px] pt-5">
        <div className="flex items-center gap-[11px]">
          <Skeleton className="h-[30px] w-[30px] rounded-[8px] bg-surface-2" />
          <Skeleton className="h-[30px] w-[70px] rounded-[8px] bg-surface-2" />
          <Skeleton className="h-5 w-[280px] rounded-[5px] bg-surface-2" />
        </div>
        <Skeleton className="ml-[41px] mt-2.5 h-3 w-[60%] rounded-[5px] bg-surface-2" />
      </div>
      <div className="flex min-h-0 flex-1 gap-0">
        {/* Espejo de rdt-list-card.tsx L243 y actividad.tsx; si cambia uno,
            cambia el otro (antes hardcodeaba 316/356 sin variantes: salto de
            layout al cargar entre 1280 y 1440px, bug preexistente). Bajo 1280 la
            Actividad real es un drawer fuera del flujo, así que el placeholder
            se oculta (review adversarial v0.13.1). */}
        <div className="flex w-[316px] flex-none flex-col gap-2 border-r border-border-subtle p-3.5 max-[1440px]:w-[280px]">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-[86px] rounded-[11px] bg-surface-2" />
          ))}
        </div>
        <div className="flex-1 p-5">
          <Skeleton className="h-[420px] rounded-[11px] bg-surface-2" />
        </div>
        <div className="w-[356px] flex-none border-l border-border-subtle p-4 max-[1440px]:w-[312px] min-[1680px]:w-[400px] min-[1920px]:w-[448px] min-[2560px]:w-[480px] max-[1279px]:hidden">
          <Skeleton className="h-[300px] rounded-[11px] bg-surface-2" />
        </div>
      </div>
    </div>
  );
}

function NoEncontrada({ cc }: { cc: string }) {
  // Hallazgo 2 (QA v0.17.0): mismo criterio que header-obra.tsx — si se
  // llegó acá desde el Avivador (rede/obra ya no existe con ese id), el
  // «Volver» tiene que ofrecer volver ahí, no a la home de Obras.
  const location = useLocation();
  const desdeAvivador = (location.state as { from?: string } | null)?.from === 'avivador';
  const volverA = desdeAvivador ? '/avivador' : '/';
  const volverLabel = desdeAvivador ? 'Volver al Avivador' : 'Volver a Obras';
  return (
    <Empty className="border-none py-16">
      <EmptyHeader>
        <EmptyTitle>La obra {cc} no existe</EmptyTitle>
      </EmptyHeader>
      <EmptyContent>
        <Button type="button" variant="outline" render={<Link to={volverA} />} nativeButton={false}>
          {volverLabel}
        </Button>
      </EmptyContent>
    </Empty>
  );
}

function ErrorObra({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex justify-center px-5 py-16">
      <Alert variant="destructive" className="max-w-sm">
        <CircleAlert aria-hidden="true" />
        <AlertTitle>No se pudo cargar la obra</AlertTitle>
        <AlertDescription>Puede ser un problema de red o del servidor. Probá de nuevo.</AlertDescription>
        <AlertAction>
          <Button type="button" size="xs" onClick={onRetry}>
            Reintentar
          </Button>
        </AlertAction>
      </Alert>
    </div>
  );
}

// Columna central cuando no hay nada seleccionable con el filtro actual (ej.
// "Pendientes" quedó vacío tras borrar la última pendiente) — mismo criterio
// visual que el empty-state de la lista izquierda (rdt-list-card.tsx), nunca
// el skeleton infinito que dejaría selId en null para siempre.
function SinSeleccion({ onVerTodas }: { onVerTodas: () => void }) {
  return (
    <Empty className="min-h-[260px] border-dashed">
      <EmptyHeader>
        <EmptyTitle>Sin RDTs pendientes</EmptyTitle>
        <EmptyDescription>Todas las redeterminaciones de esta obra están facturadas o archivadas.</EmptyDescription>
      </EmptyHeader>
      <EmptyContent>
        <Button type="button" variant="ghost" size="sm" onClick={onVerTodas}>
          Ver todas
        </Button>
      </EmptyContent>
    </Empty>
  );
}

// Página DetalleObra (`/obras/:cc`) — orquesta header + 3 columnas (lista de
// RDTs · ficha/edición · Actividad). El estado de selección/edición/paso
// abierto vive acá porque varios componentes hermanos lo comparten (ej. un
// knob del stepper en la ficha abre un form en la columna de Actividad).
export function ObraDetalle() {
  const { cc: ccParam } = useParams<{ cc: string }>();
  const cc = ccParam ? decodeURIComponent(ccParam) : '';
  const [, setSearchParams] = useSearchParams();
  const location = useLocation();
  // Snapshot de una sola vez del ?rdt= con el que se montó la página (⌘K
  // expedientes) — un ref en vez del `searchParams` reactivo del hook: el
  // efecto de selección inicial de abajo solo lo necesita en el primer
  // render, y atarlo a la dependencia reactiva lo re-dispararía en cada
  // cambio de query string sin ningún beneficio (la rama de arriba es un
  // guard de una sola vez).
  const rdtParamInicialRef = useRef(new URLSearchParams(window.location.search).get('rdt'));
  // Fix #8 (design_avivador.md §14.3): idem — el `state` de navegación con el
  // que se montó la página (`{ from: 'avivador' }` cuando se llega desde el
  // Avivador vía `irARede` en use-avivador-estado.ts) también se snapshotea en
  // un ref al montar. El efecto de selección inicial de abajo hace un
  // `setSearchParams(…, { replace: true })` para sacar `?rdt=` de la URL, y
  // ESE replace no llevaba `state` — el history.replaceState lo pisaba a
  // `null` y header-obra.tsx / NoEncontrada, que leen `useLocation().state`,
  // dejaban de ver `from: 'avivador'` y mostraban «Volver a Obras» en vez de
  // «Volver al Avivador». Se toma de un ref (no de `location.state` directo
  // en el efecto) por el mismo motivo que `rdtParamInicialRef`: no meterlo en
  // las dependencias, que re-dispararía el efecto en cada cambio de state.
  const navStateInicialRef = useRef(location.state as unknown);

  const { data: me } = useMe();
  const puedeEscribir = me?.rol === 'gestor' || me?.rol === 'admin';
  const obraQuery = useObra(cc);
  const rdtsQuery = useObraRdts(cc);

  const [verTodas, setVerTodas] = useState(false);
  const [selId, setSelId] = useState<number | null>(null);
  // Id a scrollear a la vista en la COLUMNA de cards (no confundir con
  // `resaltado`, que es el scroll+highlight del hilo de Actividad, más
  // abajo) — se setea SOLO cuando la selección inicial viene de navegación
  // (⌘K expedientes / URL con ?rdt=), nunca desde `requestSelect` (click
  // manual), para que la card ya visible que el user tocó no salte de lugar.
  const [scrollToId, setScrollToId] = useState<number | null>(null);
  const [editMode, setEditMode] = useState<EditMode>(null);
  const [pasoAbierto, setPasoAbierto] = useState<PasoAbierto>(null);
  const [actividadMobileOpen, setActividadMobileOpen] = useState(false);
  const [confirmState, setConfirmState] = useState<ConfirmDialogState | null>(null);
  // Scroll+resaltado desde la lápida/stepper hacia un ítem del hilo (puntos
  // 8/9/14 v0.9.0) — `nonce` cambia en CADA llamado (incluso repitiendo la
  // misma `clave`) para poder re-disparar el efecto de Actividad sobre el
  // mismo ítem dos veces seguidas.
  const [resaltado, setResaltado] = useState<{ clave: string; nonce: number } | null>(null);
  // Token de reset del form de alta: se incrementa cada vez que se abre
  // "Nueva" (directo o vía el guard de descarte) — EdicionRdt lo usa en su
  // `key` para remontar limpio y no heredar estado del form de edición
  // anterior (bug: "Nueva" creaba duplicados con los campos de la RDT editada).
  const [nuevaSeq, setNuevaSeq] = useState(0);

  const initializedRef = useRef(false);
  const lastIdsRef = useRef<Set<number>>(new Set());
  // Ids que eran PENDIENTES en el snapshot anterior de `rdts` — el auto-
  // "Todas" del punto 2 solo debe dispararse en la TRANSICIÓN pendiente →
  // no-pendiente (facturar/archivar recién ocurrido), nunca porque el user
  // togglée a "Pendientes" con una facturada vieja seleccionada (sin este
  // ref, el efecto rebotaría el toggle manual con un toast espurio).
  const lastPendientesRef = useRef<Set<number>>(new Set());

  const rdts = rdtsQuery.data;

  // Selección inicial (Pendientes por defecto salvo vacío) y reasignación
  // cuando la RDT seleccionada se borra de verdad — NO cuando todavía no
  // llegó el refetch de una recién creada (se distingue comparando contra
  // los ids ya vistos en el render anterior, evitando la carrera con el
  // invalidate de la mutation de alta).
  useEffect(() => {
    if (!rdts) return;
    const currentIds = new Set(rdts.map(r => r.id));

    if (!initializedRef.current) {
      initializedRef.current = true;
      // ⌘K expedientes navega a /obras/:cc?rdt=<id> para preseleccionar una
      // RDT puntual — fuerza "Todas" porque el target puede no estar entre
      // las pendientes (ej. una Facturada o Archivada encontrada por su
      // expediente viejo).
      const rdtParam = rdtParamInicialRef.current;
      const rdtIdDeQuery = rdtParam ? Number(rdtParam) : null;
      const targetDeQuery = rdtIdDeQuery != null && rdts.some(r => r.id === rdtIdDeQuery) ? rdtIdDeQuery : null;
      if (targetDeQuery !== null) {
        setVerTodas(true);
        setSelId(targetDeQuery);
        // La card llega fuera de la vista casi siempre (obras con 30+ redes,
        // caso reportado por Juan: expediente → obra 1130 → rede #28
        // seleccionada pero invisible) — fuerza el scroll de la COLUMNA.
        setScrollToId(targetDeQuery);
        setSearchParams(
          prev => {
            const next = new URLSearchParams(prev);
            next.delete('rdt');
            return next;
          },
          // Fix #8 (§14.3): conserva el state de navegación con el que se
          // montó la página — ver navStateInicialRef más arriba.
          { replace: true, state: navStateInicialRef.current },
        );
      } else {
        const pendientes = rdts.filter(esPendiente);
        const vt = pendientes.length === 0;
        setVerTodas(vt);
        setSelId(ordenarRdts(vt ? rdts : pendientes)[0]?.id ?? null);
      }
    } else if (selId !== null && lastIdsRef.current.has(selId) && !currentIds.has(selId)) {
      const visibles = ordenarRdts(verTodas ? rdts : rdts.filter(esPendiente));
      setSelId(visibles[0]?.id ?? null);
    } else if (selId !== null && !verTodas && currentIds.has(selId) && lastPendientesRef.current.has(selId)) {
      // Auto-"Todas" al perder visibilidad (punto 2 v0.9.0): la seleccionada
      // sigue viva pero ACABA de facturar o archivarse (era pendiente en el
      // snapshot anterior) — el filtro "Pendientes" la esconde igual que a
      // cualquier otra (síntoma que Juan reportó como "se archivó sola",
      // cuando en realidad nada se archivaba: era el filtro tapándola).
      // Salta a "Todas" SOLA, sin tocar la selección, con un toast que
      // explica el motivo — cubre facturar Y archivar con la misma rama.
      // El guard de transición (lastPendientesRef) evita rebotar el toggle
      // manual a "Pendientes" cuando la seleccionada ya estaba cerrada.
      const actual = rdts.find(r => r.id === selId);
      if (actual && !esPendiente(actual)) {
        setVerTodas(true);
        toast(actual.archivada ? 'Archivada — la Rede pasó a “Todas”' : 'Facturado — la Rede pasó a “Todas”');
      }
    }

    lastIdsRef.current = currentIds;
    lastPendientesRef.current = new Set(rdts.filter(esPendiente).map(r => r.id));
  }, [rdts, selId, verTodas, setSearchParams]);

  // El target de scroll+resaltado muere con la selección (review v0.9.0):
  // Actividad se desmonta/remonta al cambiar de rede (rdtDetalle &&
  // !isPending) y su efecto corre en cada mount — con un `resaltado` viejo
  // re-disparaba el scroll/highlight de la rede anterior (o un toast «no se
  // encontró ese paso») sobre la recién seleccionada.
  useEffect(() => {
    setResaltado(null);
  }, [selId]);

  const rdtQuery = useRdt(selId);
  const borrarRdt = useBorrarRdt();
  const archivarRdt = useArchivarRdt();

  function handleAbrirPaso(tipo: Exclude<TipoPaso, 'comentario'>, hitoKey?: string): void {
    setPasoAbierto({ tipo, hitoKey });
    setActividadMobileOpen(true);
  }

  function handleCerrarPaso(): void {
    setPasoAbierto(null);
  }

  // Scroll+resaltado hacia un ítem del hilo desde la lápida o el stepper
  // (puntos 8/9/14 v0.9.0) — mismo patrón que handleAbrirPaso: además de
  // setear el target, abre el drawer de Actividad en <1280px.
  function irAItem(clave: string): void {
    setResaltado(prev => ({ clave, nonce: (prev?.nonce ?? 0) + 1 }));
    // Solo abre el drawer donde el drawer EXISTE (<1280px, review v0.9.0):
    // en desktop el estado quedaba armado sin efecto visible y el overlay
    // aparecía solo al achicar la ventana después.
    if (window.matchMedia('(max-width: 1279px)').matches) {
      setActividadMobileOpen(true);
    }
  }

  function requestSelect(id: number): void {
    if (editMode) {
      setConfirmState({
        title: 'Descartar cambios',
        description: 'Hay una edición sin guardar. Si cambiás de redeterminación se pierde lo que hayas modificado.',
        confirmLabel: 'Descartar y cambiar',
        destructive: true,
        onConfirm: () => {
          setEditMode(null);
          setSelId(id);
          setPasoAbierto(null);
        },
      });
      return;
    }
    setSelId(id);
    setPasoAbierto(null);
  }

  // Mismo guard que requestSelect: "+ Nueva" con una edición (o una alta)
  // abierta debe confirmar el descarte antes de perder lo tipeado — antes
  // saltaba directo a setEditMode('new') y el form de alta heredaba el
  // estado del de edición (sin key), generando un duplicado silencioso al
  // guardar. El seq siempre se incrementa: también limpia el form si ya
  // estaba en "Nueva" (evita arrastrar un alta a medio tipear).
  function requestNueva(): void {
    if (editMode) {
      setConfirmState({
        title: 'Descartar cambios',
        description: 'Hay una edición sin guardar. Si empezás una redeterminación nueva se pierde lo que hayas modificado.',
        confirmLabel: 'Descartar y crear nueva',
        destructive: true,
        onConfirm: () => {
          setEditMode('new');
          setNuevaSeq(s => s + 1);
          setPasoAbierto(null);
        },
      });
      return;
    }
    setEditMode('new');
    setNuevaSeq(s => s + 1);
  }

  // Contrato del Esc (plan_migracion_reui.md, Ajuste 29): este keydown
  // global es el «Esc de última instancia» que cierra el paso abierto / el
  // drawer de Actividad — nunca debe pisar el Esc de una capa MÁS interna
  // (un Dialog, un AlertDialog, un listbox de Select/Command o un popover)
  // que ya lo maneja para sí misma (cerrarse a sí misma, o nada si ya la
  // canceló otro handler). Se ignora un evento con `defaultPrevented` (una
  // capa interna ya actuó) o que viene de adentro de `[role=dialog]`,
  // `[role=alertdialog]`, `[role=listbox]` o `[data-slot=popover-content]`.
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      if (e.defaultPrevented) return;
      const target = e.target;
      if (target instanceof Element && target.closest('[role="dialog"], [role="alertdialog"], [role="listbox"], [data-slot="popover-content"]')) return;
      if (confirmState) return; // el propio Dialog ya maneja su Escape
      setPasoAbierto(null);
      setActividadMobileOpen(false);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [confirmState]);

  if (obraQuery.isPending || rdtsQuery.isPending) return <DetalleSkeleton />;

  if (obraQuery.isError) {
    if (obraQuery.error instanceof ApiError && obraQuery.error.status === 404) return <NoEncontrada cc={cc} />;
    return <ErrorObra onRetry={() => void obraQuery.refetch()} />;
  }
  if (rdtsQuery.isError) {
    return <ErrorObra onRetry={() => void rdtsQuery.refetch()} />;
  }

  const obra = obraQuery.data;
  const rdtsData = rdtsQuery.data;
  const pendientes = rdtsData.filter(esPendiente);
  const rdtDetalle = rdtQuery.data;
  // CTA post-salto/calculado data-driven (decisión b Juan 2026-08-28, ver
  // ctaSiguiente() más arriba): solo con permiso de escritura, y solo si no
  // queda otra RDT del mismo tipo en ciclo.
  const cta = puedeEscribir && rdtDetalle ? ctaSiguiente(rdtDetalle, rdtsData) : null;

  function handleBorrar(): void {
    if (!rdtDetalle) return;
    const rdt = rdtDetalle;
    setConfirmState({
      title: 'Borrar redeterminación',
      description: `¿Borrar la Rede #${rdt.nro} (${rdt.tipo}) y toda su actividad (${actividadItems(rdt).length} pasos y comentarios)? Esta acción no se puede deshacer.`,
      confirmLabel: 'Borrar RDT y actividad',
      destructive: true,
      onConfirm: () => {
        borrarRdt.mutate(
          { cc: obra.cc, id: rdt.id },
          {
            onSuccess: () => toast.success(`Rede #${rdt.nro} borrada`),
            onError: err => toast.error(errorMessage(err, 'No se pudo borrar la redeterminación')),
          },
        );
      },
    });
  }

  function handleArchivar(): void {
    if (!rdtDetalle) return;
    const rdt = rdtDetalle;
    const archivar = !rdt.archivada;
    // Con confirmación + nota opcional (decisión #4 v0.9.0): el motivo del
    // archivado queda visible en el eco del hilo.
    setConfirmState({
      title: archivar ? `Archivar Rede #${rdt.nro}` : `Desarchivar Rede #${rdt.nro}`,
      description: archivar
        ? 'La Rede pasa a solo lectura y sale de las vistas por defecto — se puede desarchivar cuando haga falta.'
        : 'La Rede vuelve a estar activa y editable.',
      confirmLabel: archivar ? 'Archivar' : 'Desarchivar',
      nota: { label: 'Nota (opcional)', placeholder: archivar ? 'Motivo del archivado' : 'Motivo' },
      onConfirm: nota => {
        archivarRdt.mutate(
          { cc: obra.cc, id: rdt.id, archivar, nota },
          {
            onSuccess: () => toast.success(archivar ? `Rede #${rdt.nro} archivada` : `Rede #${rdt.nro} desarchivada`),
            onError: err => toast.error(errorMessage(err, 'No se pudo actualizar el archivado')),
          },
        );
      },
    });
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <HeaderObra obra={obra} />

      <div className="flex min-h-0 flex-1">
        <RdtListColumn
          rdts={rdtsData}
          pendientes={pendientes}
          verTodas={verTodas}
          onVerTodasChange={setVerTodas}
          selectedId={selId}
          scrollToId={scrollToId}
          onRequestSelect={requestSelect}
          onNueva={requestNueva}
        />

        <section aria-label="Ficha de la redeterminación" className="min-w-0 flex-1 overflow-y-auto px-5 pb-6 pt-4 max-[1440px]:px-4">
          {/* max-w-[760px] (Juan + panel, 2026-09-15: «achicar la lápida») —
              ficha.tsx ya es auto-fit 170px y la tabla de chequeos tiene
              overflow-x-auto, así que el cap angosto no corta nada. */}
          <div className="mx-auto max-w-[760px]">
            {editMode ? (
              <EdicionRdt
                key={editMode === 'edit' ? `edit-${rdtDetalle?.id ?? 'none'}` : `new-${nuevaSeq}`}
                obra={obra}
                rdt={editMode === 'edit' ? (rdtDetalle ?? null) : null}
                onCancelar={() => setEditMode(null)}
                onGuardado={nuevoId => {
                  setEditMode(null);
                  if (nuevoId !== undefined) setSelId(nuevoId);
                }}
              />
            ) : rdtsData.length === 0 ? (
              <Empty className="min-h-[260px] border-dashed">
                <EmptyHeader>
                  <EmptyTitle>Esta obra no tiene redeterminaciones</EmptyTitle>
                  <EmptyDescription>Cargá la primera con «Nueva».</EmptyDescription>
                </EmptyHeader>
                <EmptyContent>
                  <Button type="button" onClick={requestNueva} className="gap-1.5">
                    <Plus className="h-3.5 w-3.5" aria-hidden="true" />
                    Nueva
                  </Button>
                </EmptyContent>
              </Empty>
            ) : initializedRef.current && selId === null && !verTodas && pendientes.length === 0 ? (
              // selId reasignado a null tras borrar la última pendiente
              // (efecto de reasignación de selección) — nunca skeleton
              // infinito: la query queda disabled (id null) y jamás resuelve.
              // Gateado por initializedRef: en el primer render tras cargar
              // datos (antes de que el efecto de selección inicial corra),
              // selId también es null — sin este guard, una obra 100% sin
              // pendientes mostraría este empty-state un frame de más antes
              // de que el efecto la mande a "Todas" (parpadeo evitable).
              <SinSeleccion onVerTodas={() => setVerTodas(true)} />
            ) : rdtQuery.isError ? (
              // isError ANTES que !rdtDetalle: con la query en error, data es
              // undefined → el orden viejo hacía inalcanzable esta rama y
              // mostraba el skeleton para siempre (ej. la RDT seleccionada la
              // borró otro usuario y el GET devuelve 404).
              <ErrorObra onRetry={() => void rdtQuery.refetch()} />
            ) : rdtQuery.isPending || !rdtDetalle ? (
              <Skeleton className="h-[420px] rounded-[11px] bg-surface-2" aria-hidden="true" />
            ) : (
              <>
                {cta && (
                  <CtaProximaRdt
                    obra={obra}
                    cta={cta}
                    responsableId={rdtDetalle.responsable?.id ?? null}
                    onCreada={setSelId}
                  />
                )}
                <Ficha
                  rdt={rdtDetalle}
                  actividadCount={actividadItems(rdtDetalle).length}
                  onAbrirActividadMobile={() => setActividadMobileOpen(true)}
                  onAbrirPaso={handleAbrirPaso}
                  onEditar={() => setEditMode('edit')}
                  onBorrar={handleBorrar}
                  onArchivar={handleArchivar}
                  onIrAItem={irAItem}
                  finalizada={rdtDetalle.finalizada}
                  archivando={archivarRdt.isPending}
                  // Decisión #11b v0.9.0: borrar la RDT entera es solo del
                  // creador o un admin (migradas, creadoPorId null → solo
                  // admin) — espejo del 403 del backend, evita el botón que
                  // siempre falla.
                  puedeBorrar={me?.rol === 'admin' || (rdtDetalle.creadoPorId !== null && rdtDetalle.creadoPorId === me?.id)}
                />
              </>
            )}
          </div>
        </section>

        {rdtDetalle && !rdtQuery.isPending && (
          <Actividad
            obra={obra}
            rdt={rdtDetalle}
            currentUserId={me?.id ?? null}
            esAdmin={me?.rol === 'admin'}
            pasoAbierto={pasoAbierto}
            onAbrirPaso={handleAbrirPaso}
            onCerrarPaso={handleCerrarPaso}
            mobileOpen={actividadMobileOpen}
            onMobileClose={() => setActividadMobileOpen(false)}
            resaltado={resaltado}
          />
        )}
      </div>

      <ConfirmDialog state={confirmState} onOpenChange={open => !open && setConfirmState(null)} />
    </div>
  );
}
